<template>
  <component
    :is="wrapper"
    :class="[
      'eb-input',
      {
        'eb-input-expand': width === 'expand',
				'eb-input-inline': inline,
			},
    ]"
  >
    <EbLabel v-if="label" :forId="uid" :required="required" :infoIcon="infoIcon" :helpText="format" :text="label">
      <input
        v-if="type === 'tel'"
        :id="uid"
        :disabled="disabled"
        :type="type"
        :value="value"
        :state="state"
        :class="[state, background ? 'eb-input-simple' : 'eb-input-solid']"
        :placeholder="placeholder"
        :readonly="readonly"
        @input="onInput($event.target.value)"
        @focus="onFocus($event.target.value)"
        pattern="0[0-9]{1}(\s[0-9]{2}){4}"
        :required="required"
      />

			<input
				v-else-if="type === 'date'"
				:id="uid"
				:disabled="disabled"
				:type="type"
				:value="value"
				:placeholder="placeholder"
				:readonly="readonly"
				@input="onInput($event.target.value)"
				:required="required"
				:min="min"
				:max="max"
			/>

      <input
        v-else
        :id="uid"
        :disabled="disabled"
        :type="type"
        :value="value"
        :state="state"
        :class="[state, background ? 'eb-input-simple' : 'eb-input-solid']"
        :placeholder="placeholder"
        :readonly="readonly"
        @input="onInput($event.target.value)"
        @focus="onFocus($event.target.value)"
        :required="required"
        :min="min"
        :max="max"
      />
    </EbLabel>
  </component>
</template>

<script>
/**
 * Form Inputs are used to allow users to provide text input when the expected
 * input is short. Form Input has a range of options and supports several text
 * formats including numbers. For longer input, use the form `Textarea` element.
 */
export default {
	name: "EbInput",
	status: "ready",
	release: "1.0.0",
  props: {
    /**
     * The type of the form input field.
     * @values text, number, email
     */
    type: {
      type: String,
			default: "text",
			validator: value => {
				return value.match(/(text|number|email|tel|date)/)
			},
    },
    /**
     * Text value of the form input field.
     */
    value: {
      type: [String, Number],
			default: null,
    },
    /**
     * The placeholder value for the form input field.
     */
    placeholder: {
      type: String,
			default: null,
    },
    /**
     * The input value can't be modified.
     */
    readonly: {
      type: Boolean,
			default: false,
    },
    /**
     * The label of the form input field.
     */
    label: {
      type: String,
			default: null,
    },
    /**
     * The html element name used for the wrapper.
     * @values div, section
     */
    wrapper: {
      type: String,
			default: "div",
			validator: value => {
				return value.match(/(div|section)/)
			},
    },
    /**
     * The width of the form input field.
     * @values auto, expand
     */
    width: {
      type: String,
			default: "auto",
			validator: value => {
				return value.match(/(auto|expand)/)
			},
    },
    /**
     * The background field.
     * @values solid, simple
     */
    background: {
      type: Boolean,
			default: false,
    },
    /**
     * Whether the form input field is disabled or not.
     */
    disabled: {
      type: Boolean,
			default: false,
    },
    /**
     * Label et champ sur une seule ligne
     */
    inline: {
      type: Boolean,
			default: false,
    },
    /**
     * Manually trigger various states of the input.
     * @values hover, active, focus
     */
    state: {
      type: String,
      default: null,
			validator: value => {
				return value.match(/(hover|active|focus)/)
			},
    },
    pattern: {
      type: String,
			default: "([\\d]{2}\\s){4}[\\d]{2}",
    },
    required: {
      type: Boolean,
			default: false,
    },
    infoIcon: {
      type: Boolean,
			default: false,
    },
    helpText: {
      type: String,
			default: null,
    },
    min: {
      type: [Number, String],
			default: null,
    },
    max: {
      type: [Number, String],
			default: null,
    },
    needCondition: {
      type: Boolean,
			default: false,
		},
  },
  data() {
    return {
      format: '',
			uid: null,
		}
  },
  mounted() {
		this.uid = `uid_${this._uid}`

    if (this.helpText != '') {
			this.format = this.helpText
		}
		else {
      switch (this.type) {
				case "tel": this.format = "ex. 02 41 81 49 49"; break;
				case "email": this.format = "ex. prenom.nom@domaine.fr"; break;
				case "date": this.format = "ex. 01/01/1970"; break;
      }
    }
  },
  methods: {
    onInput(value) {
      if (this.type == 'tel') {
				value = this.formatPhoneNumber(value)
      }
			this.$emit("input", value)
    },
    onFocus(value) {
			this.$emit("focus", value)
    },
    formatPhoneNumber: function (value) {
			return value.replace(/\W/gi, '').replace(/(.{2})/g, '$1 ').replace(/ $/,'')
    }
	},
  }
</script>

<style lang="scss">
input:not([type="radio"]):not([type="checkbox"]),
select {
  border-width: $eb-border-default;
  border-style: solid;
}

.eb-input,
.eb-select {
  @include stack-space($eb-space-m);
  width: 100%;

  label {
    cursor: pointer;
    display: block;
    @include stack-space($eb-space-s);
    text-align: left;
  }

	input:not([type="radio"]):not([type="checkbox"]),
  select {
    @include inset-squish-space($eb-space-s);
    transition: all 0.2s ease;
    -webkit-appearance: none;
    appearance: none;
    font-size: $eb-font-size-xs;
    font-weight: $eb-font-weight-bold;
    border-radius: 0;
    border-width: $eb-border-default;
    border-style: solid;
    box-shadow: box-shadow-size(1);
    letter-spacing: normal;
    min-height: $eb-tappable-square;
    display: block;
    width: 100%;
    &::-webkit-input-placeholder {
      -webkit-font-smoothing: antialiased;
      font-size: $eb-font-size-xs;
      font-style: italic;
      font-weight: $eb-font-weight-regular;
      color: $eb-color-gray-700;
      opacity: 1;
    }
    &:-ms-input-placeholder {
      font-size: $eb-font-size-xs;
      font-style: italic;
      color: $eb-color-gray-700;
      opacity: 1;
      font-weight: $eb-font-weight-regular;
    }
    &::-moz-placeholder {
      -moz-osx-font-smoothing: grayscale;
      opacity: 1;
      font-size: $eb-font-size-xs;
      font-style: italic;
      color: $eb-color-gray-700;
      font-weight: $eb-font-weight-regular;
    }
    &:hover,
    &.hover {
      outline: $eb-border-default solid $eb-color-blue-secondary;
    }
    &:focus,
    &.focus {
      transition: box-shadow 0.2s ease;
      outline: $eb-border-xl solid $eb-color-blue-secondary;
      box-shadow: box-shadow-size(0);
    }
    &[disabled] {
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      cursor: not-allowed;
      background-color: $eb-color-gray-100;
      box-shadow: none;
      border-color: $eb-color-gray-600;
      opacity: 1;
    }
    &[readonly] {
      background-color: $eb-color-gray-100;
      cursor: text;
      box-shadow: none;
      border-color: $eb-color-gray-600;
      &:hover,
      &.hover,
      &:focus,
      &.focus {
        outline: 0;
      }
    }
    &:invalid {
      &:hover,
      &:focus {
        background-color: var(--eb-color-error-light);
        color: var(--eb-color-error-dark);
        outline-color: var(--eb-color-error);
      }
    }
    &:valid {
      // border: $eb-border-m solid $eb-color-success;
    }
  }
	input[type="number"] {
    max-width: 4rem;
    text-align: center;
  }

  select {
    -webkit-appearance: auto;
    appearance: auto;
    padding: 10px;
  }

  .eb-input-solid,
  .eb-select-solid {
    background: $eb-color-blue-lighter;
    border-color: $eb-color-blue-secondary;
    @include stack-space-label($eb-space-default);
  }
  .eb-input-simple,
  .eb-select-simple {
    background: $white;
    border-color: $eb-color-gray-600;
  }

	@include respond-to("medium-up") {
		input:not([type="radio"]):not([type="checkbox"]),
    select {
      width: auto;
    }
    &-expand {
      width: 100%;
			input:not([type="radio"]):not([type="checkbox"]),
      select {
        width: 100%;
      }
    }
    &-inline {
      width: fit-contento;
      display: flex;
      gap: $eb-space-m;
      .eb-input-label {
        @include stack-space($eb-space-none);
        @include inline-space($eb-space-none);
        display: flex;
        align-items: center;
      }
      .eb-input-label-content {
        flex: 1;
      }
			&.eb-input input:not([type="radio"]):not([type="checkbox"]),
      .eb-input-solid,
      .eb-input-simple,
      .eb-select-solid,
      .eb-select-simple {
        margin-top: 0;
        width: auto;
        @include inline-space-label($eb-space-default);
        //flex: 5;
      }
    }
  }
}

input[type="date"] {
  position: relative;
}

input[type="date"]::-webkit-calendar-picker-indicator {
  color: transparent;
  background: none;
  z-index: 1;
}

input[type="date"]:before {
  background: none;
  //content: url("/img/calendar-day-solid.svg");
  position: absolute;
  right: $eb-space-s;
}

@include respond-to("medium") {
  .eb-toggle-button ~ .eb-input {
    margin-left: $eb-space-l;
  }
}
</style>
